import React, {
    useEffect, useState
} from 'react';
import {
    Popover, PopoverTrigger, PopoverContent
} from 'shared/components/ui/popover';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Slider
} from 'shared/components/ui/slider';
import {
    RadioGroup, RadioGroupItem
} from 'shared/components/ui/radio-group';
import watsonxModels from '../../../json/watsonxModels';
import {
    Button
} from 'shared/components/ui/button';
import {
    Settings2,
    Info
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from 'shared/components/ui/tooltip';

const WatsonXConfiguration = ({
    parametersWatsonX, setParametersWatsonX, selectedModel
}) => {
    const [tokensLimit, setTokensLimit] = useState({
        min: 1,
        max: 2048
    });
    const [tempLimit] = useState({
        min: 0,
        max: 2
    });
    const [topKLimit] = useState({
        min: 0,
        max: 100
    });
    const [topPLimit] = useState({
        min: 0,
        max: 1
    });
    const [repetitionPenaltyLimit] = useState({
        min: 0.0,
        max: 2.0
    });

    useEffect(() => {
        if (!selectedModel.trim()) return;

        const findModel = watsonxModels.find(model => model.value === selectedModel);

        if (!findModel) return;

        if (parametersWatsonX.max_new_tokens > findModel.max_tokens) {
            setParametersWatsonX({
                ...parametersWatsonX,
                max_new_tokens: findModel.max_tokens
            });
        }
        setTokensLimit({
            min: 10,
            max: findModel.max_tokens
        });

    }, [selectedModel]);

    const handleModeChange = (value) => {
        if (value === 'greedy') {
            setParametersWatsonX({
                ...parametersWatsonX,
                temperature: 0,
                top_k: 50,
                top_p : 1,
                decoding_method: value
            });
        } else {
            // reset to default values if sample or beam_search
            setParametersWatsonX({
                ...parametersWatsonX,
                temperature: parametersWatsonX.temperature,
                top_k: parametersWatsonX.top_k,
                top_p : parametersWatsonX.top_p,
                decoding_method: value
            });
        }
    };

    const InfoIcon = ({
        content
    }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <p className="text-xs">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const renderFieldWithSlider = (
        id,
        label,
        tooltip,
        value,
        onChange,
        min,
        max,
        step,
        disabled = false
    ) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Label htmlFor={id} className={`text-sm ${disabled ? 'text-gray-400' : ''}`}>{label}</Label>
                    <InfoIcon content={tooltip} />
                </div>
                <Input
                    id={id}
                    type="number"
                    value={value}
                    step={step}
                    max={max}
                    min={min}
                    onChange={(e) => onChange(Number(e.target.value))}
                    disabled={disabled}
                    className="h-8 w-20"
                />
            </div>
            <Slider
                id={`${id}-slider`}
                value={[value]}
                onValueChange={(val) => onChange(Number(val))}
                step={step}
                max={max}
                min={min}
                disabled={disabled}
                className="w-full"
            />
        </div>
    );

    const isGreedy = parametersWatsonX.decoding_method === 'greedy';

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div className=''>
                    <Button size="icon" variant="ghost" className='w-14 h-full'>
                        <Settings2 className="h-7 w-7" />
                    </Button>
                </div>
            </PopoverTrigger>

            <PopoverContent className="w-[680px] select-none max-h-[600px] overflow-y-auto" align="center">
                <div className="grid gap-4">
                    <div className="space-y-2">
                        <h4 className="font-medium leading-none text-sm">Configure WatsonX Model</h4>
                        <p className="text-xs text-muted-foreground">
                            Set the parameters for selected WatsonX model.
                        </p>
                    </div>

                    {/* Decoding Method */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Label className="text-sm">Decoding Method</Label>
                            <InfoIcon content="Strategy for how tokens are chosen during decoding. Greedy selects the highest probability token, Sample uses probabilistic selection, Beam Search explores multiple paths." />
                        </div>
                        <RadioGroup
                            defaultValue="sample"
                            onValueChange={handleModeChange}
                            value={parametersWatsonX?.decoding_method || 'sample'}
                            className="grid grid-cols-3 gap-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="greedy" id="r1"/>
                                <Label htmlFor="r1">Greedy</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="sample" id="r2"/>
                                <Label htmlFor="r2">Sample</Label>
                            </div>
                            {/* <div className="flex items-center space-x-2">
                                <RadioGroupItem value="beam_search" id="r3"/>
                                <Label htmlFor="r3">Beam Search</Label>
                            </div> */}
                        </RadioGroup>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* Column 1 */}
                        <div className="space-y-4">
                            {/* Min New Tokens */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="minNewTokens" className="text-sm">Minimum number of tokens</Label>
                                    <InfoIcon content="Minimum number of tokens the model must generate before it can stop" />
                                </div>
                                <Input
                                    id="minNewTokens"
                                    type="number"
                                    value={parametersWatsonX.min_new_tokens || 10}
                                    step={1}
                                    min={0}
                                    max={parametersWatsonX.max_new_tokens || tokensLimit.max}
                                    onChange={(e) => {
                                        setParametersWatsonX({
                                            ...parametersWatsonX,
                                            min_new_tokens: Number(e.target.value)
                                        });
                                    }}
                                    className="h-8"
                                />
                            </div>

                            {/* Max New Tokens */}
                            {renderFieldWithSlider(
                                'maxNewTokens',
                                'Maximum number of tokens',
                                'Maximum number of tokens the model can generate',
                                parametersWatsonX.max_new_tokens,
                                (val) => setParametersWatsonX({
                                    ...parametersWatsonX,
                                    max_new_tokens: val
                                }),
                                tokensLimit.min,
                                tokensLimit.max,
                                1
                            )}

                            {/* Temperature */}
                            {renderFieldWithSlider(
                                'temperature',
                                'Temperature',
                                'Randomness of sampling — higher = more diverse; lower = more deterministic',
                                parametersWatsonX.temperature,
                                (val) => setParametersWatsonX({
                                    ...parametersWatsonX,
                                    temperature: val
                                }),
                                tempLimit.min,
                                tempLimit.max,
                                0.1,
                                isGreedy
                            )}

                            {/* Repetition Penalty */}
                            {renderFieldWithSlider(
                                'repetitionPenalty',
                                'Repetition Penalty',
                                'Penalizes repeating tokens; >1 discourages repetition',
                                parametersWatsonX.repetition_penalty || 1.0,
                                (val) => setParametersWatsonX({
                                    ...parametersWatsonX,
                                    repetition_penalty: val
                                }),
                                repetitionPenaltyLimit.min,
                                repetitionPenaltyLimit.max,
                                0.1
                            )}
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-4">
                            {/* Top K */}
                            {renderFieldWithSlider(
                                'topK',
                                'Top K',
                                'Samples only from the top-K probable tokens',
                                parametersWatsonX.top_k,
                                (val) => setParametersWatsonX({
                                    ...parametersWatsonX,
                                    top_k: val
                                }),
                                topKLimit.min,
                                topKLimit.max,
                                1,
                                isGreedy
                            )}

                            {/* Top P */}
                            {renderFieldWithSlider(
                                'topP',
                                'Top P',
                                'Nucleus sampling — considers tokens with cumulative probability ≤ p',
                                parametersWatsonX.top_p,
                                (val) => setParametersWatsonX({
                                    ...parametersWatsonX,
                                    top_p: val
                                }),
                                topPLimit.min,
                                topPLimit.max,
                                0.1,
                                isGreedy
                            )}

                            {/* Stop Sequence */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="stopSequence" className="text-sm">Stop Sequence</Label>
                                    <InfoIcon content="List of strings that stop generation when encountered. Enter multiple sequences separated by commas." />
                                </div>
                                <Input
                                    id="stopSequence"
                                    type="text"
                                    value={
                                        Array.isArray(parametersWatsonX.stop_sequence)
                                            ? parametersWatsonX.stop_sequence.join(', ')
                                            : (parametersWatsonX.stop_sequence || '')
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const sequences = value.includes(',')
                                            ? value.split(',').map(s => s.trim()).filter(s => s)
                                            : (value ? [value] : []);
                                        setParametersWatsonX({
                                            ...parametersWatsonX,
                                            stop_sequence: sequences
                                        });
                                    }}
                                    placeholder="e.g., \n\n, </END>"
                                    className="h-8"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default WatsonXConfiguration;
